
// LandingPage.js
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  createTheme,
  ThemeProvider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link
} from '@mui/material';
import { FaBullhorn, FaRobot, FaTelegramPlane, FaTwitter, FaDiscord } from 'react-icons/fa';
import Roadmap from './Roadmap'; // Import the Roadmap component
import TokenInfo from './TokenInfo'; // Import the TokenInfo component

const matrixTheme = createTheme({
  palette: {
    primary: {
      main: '#0aff0a', // Neon green
    },
    secondary: {
      main: '#00ffe7', // Neon cyan
    },
    background: {
      default: '#000000', // Dark background
      paper: '#0d0d0d',
    },
    text: {
      primary: '#0aff0a',
      secondary: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'monospace',
  },
});

export default function LandingPage() {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ThemeProvider theme={matrixTheme}>
      <Box className="matrix-background">
        {/* Hero Section */}
        <Container maxWidth="lg" className="hero-section">
          <Grid container spacing={4} alignItems="center">
            {/* LEFT SIDE HERO TEXT */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom className="title-glow">
                THE KEY MAKER
              </Typography>
              <Typography variant="h6" gutterBottom className="title-glow">
                Redefining Innovation with Community-Driven Development
              </Typography>
              <Typography variant="body1" paragraph className="description-text">
                In a rapidly evolving tech landscape, The Key Maker stands out by putting the community at the heart of innovation.
                I'm pioneering a new approach where the success of projects is determined by community interest.
                Join us to explore the power of memes in guiding project development and be part of a movement that builds what people truly want.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                className="glow-button"
                onClick={handleClickOpen}
              >
                Learn More
              </Button>
            </Grid>
            {/* RIGHT SIDE HERO IMAGE */}
            <Grid item xs={12} md={6} className="hero-image-container">
              <img src={`${process.env.PUBLIC_URL}/keymaker.webp`} alt="The Key Maker visual" className="hero-image" />
            </Grid>
          </Grid>
        </Container>

        {/* Launching Soon Dialog */}
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" className="title-glow">
            {"The Key Maker Token Has Launched!"}
          </DialogTitle>
          <DialogContent>
  <DialogContentText id="alert-dialog-description" className="description-text">
    <Typography variant="body1" paragraph>
      The Key Maker (KEYM) token is now available for you to explore. Join the revolution and be part of our innovative journey!
    </Typography>
    <Typography variant="body1" paragraph>
      <strong>Token Name:</strong> The Key Maker (KEYM)
    </Typography>
    <Typography variant="body1" paragraph>
      <strong>Contract Address:</strong> 43rFhn3AC8nTtTtksbRFWL7psRmUNoAmyizcw7qepump
    </Typography>
    <Typography variant="body1" paragraph>
      <strong>Explore KEYM:</strong>{' '}
      <Link
        href="https://pump.fun/coin/43rFhn3AC8nTtTtksbRFWL7psRmUNoAmyizcw7qepump"
        target="_blank"
        rel="noopener noreferrer"
        className="token-link"
      >
        Visit KEYM on Pump.fun
      </Link>
    </Typography>
  </DialogContentText>
</DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="primary" autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* About Section */}
        <Container maxWidth="md" className="about-section">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            className="title-glow"
          >
            About The Key Maker
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            className="description-text"
          >
            Much like the Key Maker from "The Matrix," who holds the keys to unlocking hidden pathways, I have navigated the complex world of software development, witnessing countless projects falter not due to lack of innovation, but because they failed to connect with their audience. The Key Maker is my solution to this challenge. Our mission is to unlock a new paradigm in project development by harnessing the collective power of community feedback, using meme culture as the key to understanding and guiding innovation.
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            className="description-text"
          >
            We invite you to join a vibrant network where you will have first access to groundbreaking AI projects inspired by memes.
            If a meme gains traction, it signals community interest, and the project progresses. Otherwise, we pivot to ensure we are always building what the community desires.
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            className="description-text"
          >
            This approach not only values the community's voice but also ensures that our innovations are both groundbreaking and embraced by those who will use them.
            Together, let's unlock new possibilities and redefine how tech projects are conceived and developed.
          </Typography>
        </Container>

        {/* Project Overview Section */}
        <Container maxWidth="lg" className="overview-section">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            className="title-glow"
          >
            Project Overview
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            className="description-text"
          >
            The Key Maker project is embarking on an exciting journey, leveraging the power of meme culture to foster innovation and community engagement. Here are the foundational elements we're focusing on:
          </Typography>

          <Grid container spacing={2} className="overview-cards-container">
            <Grid item xs={12} sm={6} md={4}>
              <Card className="overview-card">
                <CardContent>
                  <FaBullhorn size={30} style={{ color: '#0aff0a', marginBottom: '10px' }} />
                  <Typography variant="h6" className="card-title">
                    Meme-driven Projects
                  </Typography>
                  <Typography variant="body2" className="card-description">
                    Exploring innovative projects that utilize meme culture as a driving force, setting the stage for future announcements.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card className="overview-card">
                <CardContent>
                  <FaRobot size={30} style={{ color: '#0aff0a', marginBottom: '10px' }} />
                  <Typography variant="h6" className="card-title">
                    Technological Foundation
                  </Typography>
                  <Typography variant="body2" className="card-description">
                    Building a robust technological infrastructure to support the seamless integration of memes and advanced digital solutions.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card className="overview-card">
                <CardContent>
                  <FaBullhorn size={30} style={{ color: '#0aff0a', marginBottom: '10px' }} />
                  <Typography variant="h6" className="card-title">
                    Community Engagement
                  </Typography>
                  <Typography variant="body2" className="card-description">
                    Fostering a vibrant community through interactive platforms and collaborative events, laying the groundwork for future growth.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Token Information Section */}
        <TokenInfo />

        {/* Pump.fun Launch Section */}
        <Container maxWidth="md" className="pumpfun-section">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            className="title-glow"
          >
            Launch on Pump.fun
          </Typography>

          <Typography variant="body1" paragraph className="description-text">
            We’re proud to announce that The Key Maker will launch on{' '}
            <strong>PUMP.FUN</strong>, an innovative Solana-based platform simplifying
            token creation and trading. Here’s the quick breakdown of why Pump.fun is
            perfect for TKM:
          </Typography>

          <ul>
            <li className="description-text">
              <strong>Low-Fee Creation:</strong> Only <em>0.02 SOL</em> is required to
              create a token, ensuring a fair, low-cost entry for the community.
            </li>
            <li className="description-text">
              <strong>Bonding Curve:</strong> Tokens are automatically priced and traded
              via a smooth bonding curve. As more users buy, the price increases—
              incentivizing early adopters.
            </li>
            <li className="description-text">
              <strong>Automatic Liquidity:</strong> Once TKM’s market cap hits{' '}
              <em>$69k</em>, Pump.fun automatically injects and locks liquidity on
              Raydium, ensuring safety and deeper trading pools for everyone.
            </li>
            <li className="description-text">
              <strong>Creator Reward:</strong> When TKM completes the bonding curve
              phase, the platform rewards us (the creator) with <em>0.5 SOL</em>—supporting
              ongoing development and marketing.
            </li>
            <li className="description-text">
              <strong>Fair Launch, No Reserves:</strong> No token taxes or dev
              allocations—TKM is fully community-oriented from day one.
            </li>
            <li className="description-text">
              <strong>1% Fee:</strong> Pump.fun charges a minimal 1% fee on trades,
              powering the platform’s ecosystem.
            </li>
            <li className="description-text">
              <strong>King of the Hill:</strong> If TKM’s market cap surpasses roughly{' '}
              <em>$30k</em>, it can become “King of the Hill,” gaining free promo
              at the top of Pump.fun’s homepage for maximum visibility.
            </li>
          </ul>

          <Typography variant="body1" paragraph className="description-text">
            This unique launch process is fun, fair, and transparent—just like TKM. For a
            deeper dive into Pump.fun’s mechanics (including charts, pricing formulas, live
            streams, and bubble maps), check out the{' '}
            <strong>
              <a
                href="https://pump.fun/board"
                style={{ color: '#00ffe7', textDecoration: 'underline' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                official Pump.fun board
              </a>
            </strong>.
          </Typography>
        </Container>

        {/* Import and use the Roadmap component */}
        <Roadmap />

        {/* Community Section */}
        <Container maxWidth="lg" className="community-section">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            className="title-glow"
          >
            Join the Community
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            className="description-text"
          >
            The Key Maker is all about community and collaboration. Stay updated with the
            latest developments, memes, and AI breakthroughs by joining our vibrant social
            channels. Together, we’re writing the next chapter of meme coin history!
          </Typography>
          <Box className="button-group" display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="secondary"
              className="glow-button"
              startIcon={<FaDiscord />}
              style={{ marginRight: '1rem' }}
              href="https://discord.gg/yk9XYtTH"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discord
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className="glow-button"
              startIcon={<FaTwitter />}
              style={{ marginRight: '1rem' }}
              href="https://x.com/TheKeyMakerMC"
              target="_blank"
              rel="noopener noreferrer"
            >
              X
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className="glow-button"
              startIcon={<FaTelegramPlane />}
              href="https://t.me/TheKeyMakerHQ"
              target="_blank"
              rel="noopener noreferrer"
            >
              Telegram
            </Button>
          </Box>
        </Container>

        {/* Disclaimer Section */}
        <Container maxWidth="md" className="disclaimer-section">
          <Typography variant="h6" align="center" gutterBottom className="title-glow">
            Disclaimer
          </Typography>
          <Typography variant="body2" align="center" className="description-text">
            The content on this site is for informational and entertainment purposes only
            and does not constitute financial or investment advice. Cryptocurrency and meme
            coin investments can be highly volatile and may involve significant risk. Always
            conduct your own research, and consult with licensed professionals before making
            any investment decisions. The Key Maker is not registered as a security, and we
            make no guarantees of returns or future value. By participating in The Key Maker
            project, you acknowledge and accept the risks associated with digital currencies.
          </Typography>
        </Container>

        {/* Footer */}
        <Box className="footer-container">
          <Typography variant="body2" align="center" className="description-text">
            © {new Date().getFullYear()} The Key Maker. All Rights Reserved.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

